// WhatsApp utility functions
import { contentAPI } from '../services/api';

// Default message template (fallback if API fails)
const DEFAULT_MESSAGE = "Hola! Te contacto desde el Portal Minero Proveminer. Me interesa conocer más sobre sus servicios.";

// Cache for the default message to avoid repeated API calls
let cachedDefaultMessage = null;

/**
 * Generate WhatsApp URL for business contact
 * @param {string} phoneNumber - Business phone number 
 * @param {string} businessName - Name of the business
 * @param {string} customMessage - Custom message (optional)
 * @returns {string} WhatsApp URL
 */
export const generateWhatsAppURL = (phoneNumber, businessName, customMessage = null) => {
  if (!phoneNumber) return null;

  // Clean phone number (remove spaces, dashes, parentheses)
  let cleanPhone = phoneNumber.replace(/[\s\-\(\)\+]/g, '');

  // Ensure it starts with country code (assume Chile +56 if not present)
  if (!cleanPhone.startsWith('56') && !cleanPhone.startsWith('1') && cleanPhone.length === 9) {
    cleanPhone = '56' + cleanPhone;
  }

  // Use custom message or get default from admin settings or fallback
  const message = customMessage || getDefaultWhatsAppMessageSync(businessName);

  // Encode message for URL
  const encodedMessage = encodeURIComponent(message);

  return `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
};

/**
 * Get default WhatsApp message from admin settings
 * @param {string} businessName - Name of the business
 * @returns {Promise<string>} Default message
 */
export const getDefaultWhatsAppMessage = async (businessName) => {
  try {
    // Use cached message if available
    if (cachedDefaultMessage) {
      return cachedDefaultMessage + (businessName ? ` Específicamente sobre ${businessName}.` : '');
    }

    // Fetch from API
    const contentResponse = await contentAPI.getSection('contact', 'whatsapp_default_message');
    const message = contentResponse?.content || DEFAULT_MESSAGE;

    // Cache the message
    cachedDefaultMessage = message;

    return message + (businessName ? ` Específicamente sobre ${businessName}.` : '');
  } catch (error) {
    console.warn('Failed to fetch WhatsApp message from admin settings, using default:', error);
    return DEFAULT_MESSAGE + (businessName ? ` Específicamente sobre ${businessName}.` : '');
  }
};

/**
 * Get default WhatsApp message synchronously (for immediate use)
 * @param {string} businessName - Name of the business
 * @returns {string} Default message (cached or fallback)
 */
export const getDefaultWhatsAppMessageSync = (businessName) => {
  const message = cachedDefaultMessage || DEFAULT_MESSAGE;
  return message + (businessName ? ` Específicamente sobre ${businessName}.` : '');
};

/**
 * Open WhatsApp with business contact
 * @param {string} phoneNumber - Business phone number
 * @param {string} businessName - Name of the business
 * @param {string} customMessage - Custom message (optional)
 */
export const openWhatsAppContact = (phoneNumber, businessName, customMessage = null) => {
  const whatsappUrl = generateWhatsAppURL(phoneNumber, businessName, customMessage);

  if (whatsappUrl) {
    window.open(whatsappUrl, '_blank');
  } else {
    console.error('No se pudo generar el enlace de WhatsApp: número de teléfono inválido');
  }
};

/**
 * Check if a phone number is valid for WhatsApp
 * @param {string} phoneNumber - Phone number to validate
 * @returns {boolean} Whether the number is valid
 */
export const isValidWhatsAppNumber = (phoneNumber) => {
  if (!phoneNumber) return false;

  const cleanPhone = phoneNumber.replace(/[\s\-\(\)\+]/g, '');
  return cleanPhone.length >= 8; // Minimum valid phone number length
};

/**
 * Initialize WhatsApp settings - loads default message from admin settings
 * Should be called once when the app starts
 */
export const initWhatsAppSettings = async () => {
  try {
    await getDefaultWhatsAppMessage();
    console.log('✅ WhatsApp settings loaded');
  } catch (error) {
    console.warn('Failed to initialize WhatsApp settings:', error);
  }
};

/**
 * Clear cached WhatsApp message (useful when admin updates the message)
 */
export const clearWhatsAppCache = () => {
  cachedDefaultMessage = null;
};